import { MyReferral } from './my-social.service';

/**
 * The referral as the application shows it (Asana 1217168620947115, voted by
 * Maude on 21 October 2026): an optional field at the onboarding, and the
 * invite row of « Mes amis ».
 *
 * Pure on purpose: no Angular.
 */

/**
 * The same reading as the API's (`readReferralCode`): spaces and dashes out,
 * capitals, eight characters of an alphabet without 0, O, 1 and I. Anything
 * else is not asked to the API at all, so typing a code letter by letter
 * does not send eight requests.
 */
export const REFERRAL_CODE_LENGTH = 8;
const REFERRAL_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export const readReferralCode = (typed: unknown): string | undefined => {
  if (typeof typed !== 'string') {
    return undefined;
  }
  const code = typed.replace(/[\s-]/g, '').toUpperCase();
  return code.length === REFERRAL_CODE_LENGTH && [...code].every((c) => REFERRAL_ALPHABET.includes(c))
    ? code
    : undefined;
};

/** What the field knows about what was typed. */
export type CodeState =
  | { kind: 'empty' }
  | { kind: 'checking' }
  | { kind: 'known'; displayName?: string }
  | { kind: 'unknown' };

export interface Words {
  key: string;
  params?: Record<string, string>;
}

/**
 * The line under the field. « Code de Claire M. » for a known code, « Ce code
 * n'existe pas » for anything else that was typed, nothing while empty or
 * being checked.
 *
 * A KNOWN CODE WITHOUT A NAME says nothing: the API hides the name when a
 * block stands between the two, and saying « inconnu » would be a lie while
 * saying « bloqué » would tell the block.
 *
 * AN UNKNOWN CODE NEVER BLOCKS « Continuer » (Maude, 21/10/2026): the field is
 * optional, and somebody who got a code wrong must not lose their sign-up
 * over it.
 */
export const whatTheFieldSays = (state: CodeState): Words | undefined => {
  switch (state.kind) {
    case 'known':
      return state.displayName ? { key: 'SOCIAL.REFERRAL.CODE_OF', params: { name: state.displayName } } : undefined;
    case 'unknown':
      return { key: 'SOCIAL.REFERRAL.UNKNOWN_CODE' };
    default:
      return undefined;
  }
};

/** Whether the code is claimed after the profile is saved: only a code the API knows. */
export const theCodeToClaim = (state: CodeState, typed: unknown): string | undefined =>
  state.kind === 'known' ? readReferralCode(typed) : undefined;

/**
 * The second line of the invite row. Once the five rewards of the year are
 * reached the row says so rather than promise a joker that will not come.
 */
export const whatTheInviteRowPromises = (referral: MyReferral | undefined): Words =>
  referral && referral.rewardsLeftThisYear <= 0
    ? { key: 'SOCIAL.FRIENDS.INVITE.NO_MORE_THIS_YEAR' }
    : { key: 'SOCIAL.FRIENDS.INVITE.HINT' };

/**
 * The address shared with the code.
 *
 * WHY THE HOME PAGE AND NOT smatchy.app/parrainage/<code>, which the mock-up
 * shows: that page does not exist on the website yet, and a link to a 404 is
 * what the person who receives the invitation would see first. The code is in
 * the sentence, so nothing is lost; the address changes once the page is
 * there (Asana, follow-up of the referral).
 */
export const REFERRAL_SHARE_URL = 'https://smatchy.app';
